import { cloneElement, MouseEvent, ReactElement, ReactNode, Ref, SVGProps } from 'react';
import Link from 'next/link';
import clsx from 'clsx';

type Variation = 'shadow' | 'flat' | 'outline' | 'outline-white' | 'navlink' | 'trim';
type Size = 'trim' | 'sm' | 'md' | 'lg';
type Position = 'left' | 'right';
type CtaType = 'button' | 'submit';

type CtaInnerProps = { children?: ReactNode; text?: string; icon?: ReactNode };

type CtaProps = Omit<CtaInnerProps, 'icon'> & {
  ref?: Ref<HTMLElement>;
  icon?: ReactElement<SVGProps<SVGSVGElement>>;
  variation?: Variation;
  onClick?: (e?: MouseEvent<HTMLElement>) => void;
  className?: string;
  href?: string;
  size?: Size;
  iconSide?: Position;
  disabled?: boolean;
  type?: CtaType;
  anchor?: boolean;
};

export const baseClassName = 'brand-button';

export const variationClassName: Record<Variation, string> = {
  shadow: 'bg-gradient-brand-button shadow-brand-button text-white hover:brightness-90',
  flat: 'bg-brand-button  text-white hover:brightness-90',
  outline: 'hover:bg-black/10 ring-inset ring-1 ring-brand-button text-brand-button',
  'outline-white': 'ring-1 hover:bg-black/10 ring-white text-white ring-inset',
  navlink: 'hover:bg-[#F3F4F6] px-4',
  trim: '',
};

export const sizeClassName: Record<Size, string> = {
  trim: '',
  sm: 'text-sm gap-1 leading-3 py-2 px-3 @lg:gap-2 @lg:text-lg @lg:leading-4 @lg:tracking-[-0.54px]',
  md: 'p-4 text-base leading-4',
  lg: 'p-5 text-base @lg:text-lg leading-4.5 tracking-[-0.54px] @lg:text-xl @lg:leading-5 @lg:tracking-[-0.6px]',
};

export const iconSizeClassName: Record<Size, string> = {
  trim: '',
  sm: 'w-4 h-4 shrink-0 @lg:w-4.5 @lg:h-4.5',
  md: 'w-4 h-4 shrink-0 @lg:w-4 @lg:h-4',
  lg: 'w-4.5 h-4.5 shrink-0 @lg:w-5 @lg:h-5',
};

export const iconPositionClassName: Record<Position, string> = {
  right: 'flex-row',
  left: 'flex-row-reverse',
};

function CtaInner({ children, text, icon }: CtaInnerProps) {
  return (
    <>
      {children}
      {text && <span className="pointer-events-none">{text}</span>}
      {icon}
    </>
  );
}

export function Cta({
  ref,
  children,
  className,
  href,
  onClick,
  text,
  icon,
  disabled,
  type,
  variation = 'shadow',
  size = 'lg',
  iconSide = 'right',
  anchor = false,
  ...props
}: CtaProps) {
  const iconElement = icon
    ? cloneElement(icon, {
        className: clsx(iconSizeClassName[size], icon.props.className),
      })
    : null;

  if (href) {
    const LinkComponent = anchor ? 'a' : Link;
    return (
      <LinkComponent
        suppressHydrationWarning
        ref={ref as Ref<HTMLAnchorElement>}
        href={href}
        className={clsx(
          baseClassName,
          iconPositionClassName[iconSide],
          sizeClassName[size],
          variationClassName[variation],
          className
        )}
        onClick={onClick}
        {...props}
      >
        <CtaInner text={text} icon={iconElement}>
          {children}
        </CtaInner>
      </LinkComponent>
    );
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      className={clsx(
        baseClassName,
        iconPositionClassName[iconSide],
        sizeClassName[size],
        variationClassName[variation],
        className
      )}
      onClick={onClick}
      disabled={disabled}
      type={type}
      {...props}
    >
      <CtaInner text={text} icon={iconElement}>
        {children}
      </CtaInner>
    </button>
  );
}

export default Cta;
